import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * One product card within a Shop the Look outfit. Always shows image,
 * title, and price; clicking the card expands its size/color selectors,
 * individual "Add to cart", and product link. Resolves the selected
 * option combination to a real variant client-side (no network request),
 * and exposes getSelection() — which reads the hidden variant input
 * regardless of whether the card is expanded — so the parent
 * <shop-the-look-outfit> can collect every item for the bulk add using
 * whatever the customer last selected.
 *
 * @typedef {object} ShopTheLookItemRefs
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLButtonElement} [summaryToggle]
 * @property {HTMLElement} [detailsPanel]
 * @property {HTMLInputElement} [variantIdInput]
 * @property {HTMLElement} [priceEl]
 * @property {HTMLElement} [statusEl]
 * @property {HTMLScriptElement} [variantData]
 * @property {HTMLSelectElement[]} [optionSelect]
 * @property {HTMLElement} [slideshowTrack]
 * @property {HTMLButtonElement} [prevImageButton]
 * @property {HTMLButtonElement} [nextImageButton]
 * @extends Component<ShopTheLookItemRefs>
 */
class ShopTheLookItemComponent extends Component {
  /** @type {Array<Record<string, unknown>>} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.refs.variantData;
    if (dataEl) {
      try {
        this.#variants = JSON.parse(dataEl.textContent || '[]');
      } catch {
        this.#variants = [];
      }
    }

    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.isArray(this.refs.optionSelect) ? this.refs.optionSelect : this.refs.optionSelect ? [this.refs.optionSelect] : []
    );
    selects.forEach((select) => select.addEventListener('change', this.#onOptionChange));

    this.refs.addButton?.addEventListener('click', this.#onAddToCart);
    this.refs.summaryToggle?.addEventListener('click', this.#onToggle);
    this.refs.prevImageButton?.addEventListener('click', this.#onPrevImage);
    this.refs.nextImageButton?.addEventListener('click', this.#onNextImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.isArray(this.refs.optionSelect) ? this.refs.optionSelect : this.refs.optionSelect ? [this.refs.optionSelect] : []
    );
    selects.forEach((select) => select.removeEventListener('change', this.#onOptionChange));

    this.refs.addButton?.removeEventListener('click', this.#onAddToCart);
    this.refs.summaryToggle?.removeEventListener('click', this.#onToggle);
    this.refs.prevImageButton?.removeEventListener('click', this.#onPrevImage);
    this.refs.nextImageButton?.removeEventListener('click', this.#onNextImage);
  }

  /**
   * @returns {{ variantId: string | null, available: boolean, quantity: number }}
   */
  getSelection() {
    const variantId = this.refs.variantIdInput?.value || null;
    const variant = this.#variants.find((v) => String(v.id) === String(variantId));
    const available = variant ? Boolean(variant.available) : Boolean(variantId);

    return { variantId, available, quantity: 1 };
  }

  #onToggle = () => {
    const panel = this.refs.detailsPanel;
    const toggle = this.refs.summaryToggle;
    if (!panel || !toggle) return;

    const expanded = !panel.hidden;
    panel.hidden = expanded;
    toggle.setAttribute('aria-expanded', String(!expanded));
  };

  /**
   * @param {MouseEvent} event
   */
  #onPrevImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.refs.slideshowTrack?.scrollBy({ left: -this.refs.slideshowTrack.clientWidth, behavior: 'smooth' });
  };

  /**
   * @param {MouseEvent} event
   */
  #onNextImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.refs.slideshowTrack?.scrollBy({ left: this.refs.slideshowTrack.clientWidth, behavior: 'smooth' });
  };

  #onOptionChange = () => {
    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.isArray(this.refs.optionSelect) ? this.refs.optionSelect : this.refs.optionSelect ? [this.refs.optionSelect] : []
    );
    const selected = selects
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((select) => select.value);

    const match = this.#variants.find((variant) => {
      for (let i = 0; i < selected.length; i++) {
        if (variant[`option${i + 1}`] !== selected[i]) return false;
      }
      return true;
    });

    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = match ? String(match.id) : '';
    }

    if (this.refs.priceEl && match && typeof match.price === 'number') {
      this.refs.priceEl.textContent = this.#formatMoney(match.price);
    }

    if (this.refs.addButton) {
      this.refs.addButton.disabled = !match || !match.available;
    }

    this.#setStatus('');
  };

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });
  }

  /**
   * @param {string} message
   * @param {'error' | ''} [state]
   */
  #setStatus(message, state = '') {
    if (!this.refs.statusEl) return;
    this.refs.statusEl.textContent = message;
    this.refs.statusEl.hidden = !message;
    if (state) {
      this.refs.statusEl.dataset.state = state;
    } else {
      delete this.refs.statusEl.dataset.state;
    }
  }

  #onAddToCart = async () => {
    const { variantId, available } = this.getSelection();

    if (!variantId || !available) {
      this.#setStatus('Select a size to continue.', 'error');
      return;
    }

    const button = this.refs.addButton;
    if (button) button.disabled = true;

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        ...fetchConfig('json'),
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#setStatus(data.description || data.message || 'Could not add this item.', 'error');
        if (button) button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: Promise.resolve({ detail: { didError: false } }),
        })
      );

      if (button) {
        button.dataset.added = 'true';
        setTimeout(() => {
          button.dataset.added = '';
          button.disabled = false;
        }, 2000);
      }
    } catch {
      this.#setStatus('Something went wrong adding this item.', 'error');
      if (button) button.disabled = false;
    }
  };
}

/**
 * One outfit. Collects every nested <shop-the-look-item>'s current
 * selection and adds them all to the cart in a single request.
 *
 * @typedef {object} ShopTheLookOutfitRefs
 * @property {HTMLButtonElement} [addLookButton]
 * @property {HTMLElement} [addLookStatus]
 * @property {HTMLElement} [outfitSlideshowTrack]
 * @property {HTMLButtonElement} [outfitPrevImageButton]
 * @property {HTMLButtonElement} [outfitNextImageButton]
 * @extends Component<ShopTheLookOutfitRefs>
 */
class ShopTheLookOutfitComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.refs.addLookButton?.addEventListener('click', this.#onAddEntireLook);
    this.refs.outfitPrevImageButton?.addEventListener('click', this.#onPrevOutfitImage);
    this.refs.outfitNextImageButton?.addEventListener('click', this.#onNextOutfitImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.addLookButton?.removeEventListener('click', this.#onAddEntireLook);
    this.refs.outfitPrevImageButton?.removeEventListener('click', this.#onPrevOutfitImage);
    this.refs.outfitNextImageButton?.removeEventListener('click', this.#onNextOutfitImage);
  }

  /**
   * @param {MouseEvent} event
   */
  #onPrevOutfitImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.refs.outfitSlideshowTrack?.scrollBy({ left: -this.refs.outfitSlideshowTrack.clientWidth, behavior: 'smooth' });
  };

  /**
   * @param {MouseEvent} event
   */
  #onNextOutfitImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.refs.outfitSlideshowTrack?.scrollBy({ left: this.refs.outfitSlideshowTrack.clientWidth, behavior: 'smooth' });
  };

  /**
   * @param {string} message
   * @param {'error' | 'success' | ''} [state]
   */
  #setStatus(message, state = '') {
    if (!this.refs.addLookStatus) return;
    this.refs.addLookStatus.textContent = message;
    this.refs.addLookStatus.hidden = !message;
    if (state) {
      this.refs.addLookStatus.dataset.state = state;
    } else {
      delete this.refs.addLookStatus.dataset.state;
    }
  }

  #onAddEntireLook = async () => {
    const items = /** @type {ShopTheLookItemComponent[]} */ (Array.from(this.querySelectorAll('shop-the-look-item')));

    if (items.length === 0) {
      this.#setStatus('No products in this look yet.', 'error');
      return;
    }

    const selections = items.map((item) => item.getSelection());
    const incomplete = selections.some((selection) => !selection.variantId || !selection.available);

    if (incomplete) {
      this.#setStatus('Select a size for every item before adding the whole look.', 'error');
      return;
    }

    const button = this.refs.addLookButton;
    if (button) button.disabled = true;
    this.#setStatus('Adding look to cart…');

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        ...fetchConfig('json'),
        body: JSON.stringify({
          items: selections.map((selection) => ({ id: selection.variantId, quantity: selection.quantity })),
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#setStatus(data.description || data.message || 'Could not add this look to your cart.', 'error');
        if (button) button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: selections.map((selection) => ({ merchandiseId: /** @type {string} */ (selection.variantId), quantity: selection.quantity })),
          promise: Promise.resolve({ detail: { didError: false } }),
        })
      );

      this.#setStatus('Look added to cart.', 'success');
    } catch {
      this.#setStatus('Something went wrong adding this look.', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  };
}

customElements.define('shop-the-look-item', ShopTheLookItemComponent);
customElements.define('shop-the-look-outfit', ShopTheLookOutfitComponent);
